/**
 * The add card, the suggestion row and the progress bar of the cart (PDP-#14,
 * #19, #20), in the drawer and on the cart page.
 *
 * Jobs:
 * 1. measurement - CartUpsellView, CartUpsellClick, CartUpsellAdd,
 *    CartUpsellDismiss and CartGiftProgress to the Cortex pixel. The app
 *    extension (engine repo, extensions/cortex-cart-upsell/assets/cortex-cart-upsell.js)
 *    sends the same events with the same payload shape, the same rule
 *    vocabulary (kleur/maat/accessoire), the same strategy vocabulary (same_design /
 *    accessory, see RULE_TO_STRATEGY below) and the same line item property
 *    (_cortex_upsell), so an order or an Add event reads the same way whichever
 *    origin produced it. Every event this script sends also carries
 *    source:'theme' (see track() below), so a consumer can tell the theme's
 *    block apart from the app's (source:'app');
 * 2. adding with one click - the add card, an accessory in the row and a hat
 *    for the free hat offer all go in through the same fetch (addFromButton),
 *    each with its own _cortex_upsell value (card / accessory / gift);
 * 3. carrying the rule into a quick add - a sweater in the row opens the
 *    shared quick-add-drawer for its sizes. That form is fetched from the
 *    product page and has no property input of its own, so the rule is
 *    injected in a capture-phase submit listener, which runs before the
 *    listener product-form.js binds on the form. After the add the visitor
 *    goes back to the cart drawer, where the new line and the progress are;
 * 4. one promise at a time - while the free hat is earned (Cortex, see
 *    assets/jc-cortex.js renderCartProgress) the paid hat steps aside: the add
 *    card and any card of the row that offers one of the free hats;
 * 5. showing the gift tile the visitor picked in the popup (localStorage
 *    jap_gift_choice, written by WP6).
 *
 * Everything is delegated on document, because cart-drawer.js replaces the whole
 * innerHTML of <cart-drawer> on every add and cart-items.js replaces the blocks on
 * every quantity change. Binding to elements would not survive that.
 *
 * LOCK AGAINST DOUBLE-RUN WITH THE APP EXTENSION.
 * The Cortex cart upsell app extension (theme-app-extension, package W1) renders the
 * same suggestions and the same gift progress through its own widget. Both blocks
 * running at once would show two upsells in the same drawer and fire CartUpsellAdd
 * twice for the same line - a double count that cannot be corrected after the fact.
 * The lock is therefore code: this script checks appSourceActive() before it
 * registers a single listener or observer, and returns immediately when the app is
 * active.
 *
 * Load order and why the check works:
 * - the app embed writes document.documentElement.setAttribute('data-cortex-upsell-
 *   source', 'app') and window.CortexCartUpsellSource from an inline <script> that
 *   runs while the document is still parsing;
 * - this file is loaded with the defer attribute, so it only executes after the whole
 *   document has been parsed;
 * - the flag is therefore always set before this script's IIFE runs when the app is
 *   present on the page, and appSourceActive() catches it on the very first check.
 *
 * The reverse order - theme active first, app arriving later - only exists if this
 * script is ever made non-defer, or if the app embed is injected into the page after
 * load. For that case the app also dispatches the document event
 * 'cortex:upsell:source' when it takes over. When this script initialised as the
 * active theme source, it listens for that one event and tears itself down
 * completely: every listener and the MutationObserver are removed.
 */
(() => {
  // Safe against a double include: the drawer and the cart page both load this file.
  if (window.cartSetUpsellLoaded) return;
  window.cartSetUpsellLoaded = true;

  const SOURCE_ATTRIBUTE = 'data-cortex-upsell-source';
  const SOURCE_EVENT = 'cortex:upsell:source';

  /**
   * True when the Cortex cart upsell app extension has taken over rendering. Checks
   * both signals the app sets during page parse (see the load-order note above).
   * @returns {boolean}
   */
  function appSourceActive() {
    if (document.documentElement.getAttribute(SOURCE_ATTRIBUTE) === 'app') return true;
    if (window.CortexCartUpsellSource && window.CortexCartUpsellSource.source === 'app') return true;
    return false;
  }

  if (appSourceActive()) {
    console.info('[cart-set-upsell] disabled: app source active');
    return;
  }

  console.info('[cart-set-upsell] source=theme');

  const RULE_ATTRIBUTE = 'data-upsell-rule';
  const PROPERTY_NAME = 'properties[_cortex_upsell]';
  const GIFT_CHOICE_KEY = 'jap_gift_choice';
  const DISMISS_KEY = 'jc-add-card-dismissed';

  /**
   * Maps a theme rule to the strategy vocabulary the engine app writes into
   * _cortex_upsell (see the engine repo, extensions/cortex-cart-upsell/assets/
   * cortex-cart-upsell.js). The theme keeps its own rule only in RULE_ATTRIBUTE,
   * for its on-page analytics.
   */
  const RULE_TO_STRATEGY = {
    kleur: 'same_design',
    maat: 'same_design',
    accessoire: 'accessory'
  };

  /** Rule of the card the visitor last clicked, waiting for a quick-add submit. */
  let pendingRule = null;

  /** Rules already reported as seen during the current drawer opening. */
  let seenRules = new Set();

  /** True once CartGiftProgress has been sent for this session. */
  let giftProgressReported = false;

  /** MutationObserver watching the containers that get their innerHTML replaced. */
  let rerenderObserver = null;

  /**
   * Sends an event to the Cortex pixel, queueing it when the pixel has not loaded yet.
   * Stamps source:'theme' on every payload.
   * @param {string} name - Event name.
   * @param {object} data - Event payload.
   */
  function track(name, data) {
    const payload = Object.assign({}, data, { source: 'theme' });
    if (window.NanoPixel) {
      window.NanoPixel.track(name, payload);
    } else {
      window.NanoPixelQueue = window.NanoPixelQueue || [];
      window.NanoPixelQueue.push([name, payload]);
    }
  }

  /**
   * Reports, per rule, the cards of the row that are visible and not reported yet
   * during this drawer opening.
   */
  function reportViews() {
    document.querySelectorAll('cart-set-upsell').forEach((row) => {
      const handlesPerRule = new Map();
      row.querySelectorAll(`.cart-set-upsell__card[${RULE_ATTRIBUTE}]:not([hidden])`).forEach((card) => {
        const rule = card.getAttribute(RULE_ATTRIBUTE);
        if (!handlesPerRule.has(rule)) handlesPerRule.set(rule, []);
        handlesPerRule.get(rule).push(card.dataset.handle);
      });

      handlesPerRule.forEach((handles, rule) => {
        if (seenRules.has(rule)) return;
        seenRules.add(rule);
        track('CartUpsellView', {
          rule,
          products: handles.join(','),
          context: row.dataset.context || 'drawer'
        });
      });
    });
  }

  /**
   * Reports the free hat threshold once it is reached, once per session.
   */
  function reportGiftProgress() {
    if (giftProgressReported) return;

    const progress = document.querySelector('[data-cortex-gift-progress][data-gift-reached="true"]');
    if (!progress) return;

    giftProgressReported = true;
    track('CartGiftProgress', {
      thresholdType: progress.dataset.thresholdType,
      threshold: Number(progress.dataset.threshold),
      progress: Number(progress.dataset.progress),
      context: progress.dataset.context
    });
  }

  /**
   * Renders the gift tile the visitor picked in the popup, if there is one.
   */
  function renderGiftChoice() {
    const slots = document.querySelectorAll('[data-gift-choice-slot]');
    if (slots.length === 0) return;

    let choice = null;
    try {
      choice = JSON.parse(window.localStorage.getItem(GIFT_CHOICE_KEY) || 'null');
    } catch (error) {
      choice = null;
    }

    slots.forEach((slot) => {
      let label = '';

      if (choice && choice.tile) {
        let tiles = {};
        try {
          tiles = JSON.parse(slot.dataset.giftTiles || '{}');
        } catch (error) {
          tiles = {};
        }

        const name = tiles[choice.tile];
        const template = slot.dataset.giftChoiceTemplate || '';
        if (name && template) label = template.replace('%GIFT%', name);
      }

      slot.textContent = label;
      slot.hidden = label === '';
    });
  }

  /**
   * One promise at a time. The add card hides when the visitor said no this
   * session. While the free hat is earned, the add card and every card of the
   * row that offers one of the free hats step aside: a paid hat next to a free
   * one is a contradiction. A row left without visible cards hides as a whole.
   */
  function applyPromiseState() {
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      dismissed = false;
    }

    const progress = document.querySelector('[data-cortex-gift-progress][data-gift-reached="true"]');
    const freeHatIds = progress ? String(progress.dataset.giftProductIds || '').split(',').filter(Boolean) : [];

    document.querySelectorAll('[data-cart-add-card]').forEach((card) => {
      card.hidden = dismissed || Boolean(progress);
    });

    document.querySelectorAll('cart-set-upsell').forEach((row) => {
      let visible = 0;
      row.querySelectorAll('.cart-set-upsell__card').forEach((card) => {
        card.hidden = freeHatIds.includes(String(card.dataset.productId));
        if (!card.hidden) visible += 1;
      });
      row.hidden = visible === 0;
    });
  }

  /**
   * Adds the rule as a line item property to a form that does not have one yet, in
   * the app's strategy vocabulary (see RULE_TO_STRATEGY).
   * @param {HTMLFormElement} form - Form being submitted.
   * @param {string} rule - Upsell rule.
   */
  function addRuleToForm(form, rule) {
    if (form.querySelector(`[name="${PROPERTY_NAME}"]`)) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = PROPERTY_NAME;
    input.value = RULE_TO_STRATEGY[rule] || rule;
    form.appendChild(input);
  }

  /**
   * Handles clicks on a card of the row anywhere in the document.
   * @param {object} evt - Event object.
   */
  function handleClick(evt) {
    // A quick add opens the shared quick-add-drawer, so ANY click on .js-quick-add
    // decides pendingRule fresh: to a rule when the click came from the row, to null
    // otherwise. Without this, a rule from an abandoned quick add would be stamped
    // onto the next, unrelated quick-add submit.
    const quickAdd = evt.target.closest ? evt.target.closest('.js-quick-add') : null;
    if (quickAdd) {
      const quickAddCard = quickAdd.closest(`.cart-set-upsell__card[${RULE_ATTRIBUTE}]`);
      pendingRule = quickAddCard ? quickAddCard.getAttribute(RULE_ATTRIBUTE) : null;
    }

    const card = evt.target.closest ? evt.target.closest(`.cart-set-upsell__card[${RULE_ATTRIBUTE}]`) : null;
    if (!card) return;

    const trigger = evt.target.closest('.js-quick-add, [data-upsell-add], a');
    if (!trigger) return;

    track('CartUpsellClick', {
      rule: card.getAttribute(RULE_ATTRIBUTE),
      products: card.dataset.handle || '',
      href: trigger.tagName === 'A' ? trigger.getAttribute('href') : null
    });
  }

  /**
   * Injects the pending rule into the quick add form before product-form.js reads it.
   * Capture phase, so it runs before the form's own submit listener.
   * @param {object} evt - Event object.
   */
  function handleSubmitCapture(evt) {
    const form = evt.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!pendingRule) return;
    if (!form.closest('quick-add-drawer')) return;

    addRuleToForm(form, pendingRule);
  }

  /**
   * Reports an add that came through a quick add from the row, recognised by the
   * _cortex_upsell property, and brings the visitor back to the cart drawer.
   * product-form.js re-renders the drawer 700 ms after the add; opening it a little
   * later shows the new line and the progress, and closes the quick-add drawer.
   * Payload shape ({strategy, handle, contentIds}) matches the engine app's
   * CartUpsellAdd.
   * @param {object} evt - Event object.
   */
  function handleCartAdd(evt) {
    const detail = evt.detail || {};
    const cart = detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    const line = cart.items.find(
      (item) => item.variant_id === detail.variantId && item.properties && item.properties._cortex_upsell
    );
    if (!line) return;

    pendingRule = null;
    track('CartUpsellAdd', {
      strategy: line.properties._cortex_upsell,
      handle: line.handle,
      contentIds: [String(line.variant_id)]
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const fromQuickAdd = evt.target && evt.target.closest && evt.target.closest('quick-add-drawer');
    if (cartDrawer && fromQuickAdd) window.setTimeout(() => cartDrawer.open(), 900);
  }

  /**
   * One click adds: the add card ('card'), an accessory in the row ('accessory')
   * and a hat for the free hat offer ('gift'). The product goes in at its normal
   * price with the strategy as _cortex_upsell; a free hat becomes free through the
   * Shopify discount, never through the theme. A fetch and not a form: on /cart
   * these blocks sit inside form#cart, and a form inside a form breaks the
   * checkout button.
   * @param {object} evt - Event object.
   */
  async function handleUpsellAdd(evt) {
    const button = evt.target.closest ? evt.target.closest('[data-upsell-add]') : null;
    if (!button || button.disabled) return;
    evt.preventDefault();
    button.disabled = true;
    button.classList.add('is-loading');

    const strategy = button.dataset.strategy;
    const drawer = document.querySelector('cart-drawer');
    const drawerSection = drawer ? drawer.closest('.shopify-section') : null;
    const sections = ['cart-icon-bubble'];
    if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _cortex_upsell: strategy } }],
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();

      track('CartUpsellAdd', {
        strategy,
        handle: button.dataset.handle || null,
        contentIds: [String(button.dataset.variantId)]
      });

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) {
        bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
      const cartItems = document.querySelector('cart-items');
      if (window.location.pathname === theme.routes.cart && cartItems) {
        cartItems.refresh();
      } else if (drawer) {
        drawer.renderContents(data, false);
      }
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      if (drawer) drawer.refresh(true);
    }
  }

  /** "Nee, bedankt" on the add card: away for the rest of the session. */
  function handleAddCardDismiss(evt) {
    const button = evt.target.closest ? evt.target.closest('[data-add-card-dismiss]') : null;
    if (!button) return;
    evt.preventDefault();
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      // Private mode: the card simply comes back on the next render.
    }
    track('CartUpsellDismiss', { strategy: 'card' });
    applyPromiseState();
  }

  /**
   * Re-reads the blocks after the cart drawer or the cart page was re-rendered, or
   * after assets/jc-cortex.js filled the progress bar.
   */
  function refresh() {
    applyPromiseState();
    renderGiftChoice();
    reportGiftProgress();
    if (document.querySelector('cart-drawer[aria-hidden="false"]') || !document.querySelector('cart-drawer')) {
      reportViews();
    }
  }

  /**
   * Watches the containers that get their innerHTML replaced, so the blocks are
   * picked up again after cart-drawer.js or cart-items.js re-renders them.
   */
  function observeRerenders() {
    if (!('MutationObserver' in window)) return;

    let timer = null;
    rerenderObserver = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(refresh, 100);
    });

    ['#cart-drawer', '.cc-main-cart'].forEach((selector) => {
      const root = document.querySelector(selector);
      if (root) rerenderObserver.observe(root, { childList: true, subtree: true });
    });
  }

  /**
   * Closing the quick-add-drawer (data-name="quickbuy") without adding invalidates
   * the pending rule, so it can never survive into a later, unrelated submit.
   */
  function handleQuickbuyAfterClose() {
    pendingRule = null;
  }

  function handleDrawerAfterOpen() {
    seenRules = new Set();
    refresh();
  }

  function handleDrawerAfterClose() {
    seenRules = new Set();
  }

  /** The cart page has no drawer, so the first view is reported on load. */
  function handleDomContentLoaded() {
    observeRerenders();
    refresh();
  }

  const listeners = [
    ['click', handleClick],
    ['click', handleUpsellAdd],
    ['click', handleAddCardDismiss],
    ['jc-cortex:applied', refresh],
    ['on:cart:add', handleCartAdd],
    ['on:quickbuy:after-close', handleQuickbuyAfterClose],
    ['on:cart-drawer:after-open', handleDrawerAfterOpen],
    ['on:cart-drawer:after-close', handleDrawerAfterClose]
  ];

  /**
   * Reverse-order handling: the theme initialised first and only afterwards did the
   * app extension take over (see the load-order note in the file header). Removes
   * every listener and the MutationObserver this script registered.
   */
  function teardown() {
    listeners.forEach(([name, handler]) => document.removeEventListener(name, handler));
    document.removeEventListener('submit', handleSubmitCapture, true);
    document.removeEventListener('DOMContentLoaded', handleDomContentLoaded);
    document.removeEventListener(SOURCE_EVENT, teardown);

    if (rerenderObserver) {
      rerenderObserver.disconnect();
      rerenderObserver = null;
    }

    seenRules = new Set();
    pendingRule = null;

    console.info('[cart-set-upsell] torn down: app source arrived');
  }

  listeners.forEach(([name, handler]) => document.addEventListener(name, handler));
  document.addEventListener('submit', handleSubmitCapture, true);

  // Registered once, for the reverse load order only - see the file header.
  document.addEventListener(SOURCE_EVENT, teardown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDomContentLoaded);
  } else {
    observeRerenders();
    refresh();
  }
})();
